import { ResponsiveMargin } from '@/components/atoms/legacy/ResponsiveMargin/ResponsiveMargin';
import { EVSavingsToggle } from '@/components/molecules/EVSavingsToggle/EVSavingsToggle';
import { useZipContext } from '@/context/ZipContext';
import { EMPTY_FILTERS } from '@/lib/constants';
import { parseListingVehicle } from '@/lib/parseListingVehicle';
import {
  VehicleListingModel,
  VehicleListingType,
} from '@/lib/schema/inventory/types';
import { mapFilterValuesToQueryParamsString } from '@/lib/searchFilters';
import { showToast } from '@/lib/toast';
import { FilterType } from '@/types/filters';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import SavingsToolV2 from '@/components/template/SavingsToolV2/SavingsToolV2NoRebates';
import { putEditableDealerInfo } from '../../../lib/schema/dealer/client-fetch';
import { EditableDealerInfo } from '../../../lib/schema/dealer/types';
import GrayedOut from '../../atoms/GrayedOut';

import Benefits from './DealerBenefits/DealerBenefits';
import {
  defaultBenefits,
  defaultDetailedBenefits,
} from './DealerBenefits/defaults';
import DealerEditHeader from './DealerEditHeader';
import { DealerHeader } from './DealerHeader';
import DealerInventory, {
  DealerInventoryProps,
  FilterOverlay,
  PAGE_SIZE,
} from './DealerInventory';
import DealerLocation, { LocationInfo } from './DealerLocation/DealerLocation';
import { defaultHours } from './DealerLocation/Hours';

export interface DealerV2TemplateProps {
  editable: boolean;
  dealer: {
    id: string;
    name: string;
    makes: string[];
    location: LocationInfo;
    phone: string;
    website?: string;
  };
  dealerMetadata: EditableDealerInfo;
  availableMakes: string[];
}

const DealerV2Template: React.FC<DealerV2TemplateProps> = ({
  editable,
  dealer,
  dealerMetadata,
  availableMakes,
}) => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<FilterType>({
    ...EMPTY_FILTERS,
    dealer: dealer.name,
    limit: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
    sort: 'dateinstock_desc',
  });
  const { loading: zipLoading, preferredZip } = useZipContext();
  const [iframeVehicle, setIframeVehicle] = useState<VehicleListingType>();

  const { data: availableModels } = useQuery({
    queryKey: ['dealer-profile-models', filters.make, dealer.id],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        makes: filters.make.join(','),
        dealershipId: dealer.id,
      });

      const res = await fetch(
        `/api/inventory/listings/models?${searchParams.toString()}`
      );
      if (!res.ok) {
        throw new Error();
      }

      return (await res.json()) as Record<string, string[]>;
    },
    enabled: filters.make.length > 0,
  });

  const { data: inventory, isLoading } = useQuery({
    queryKey: ['dealer-profile-listings', filters, page, preferredZip],
    queryFn: async () => {
      const queryString = mapFilterValuesToQueryParamsString({
        ...filters,
        skip: (page - 1) * PAGE_SIZE,
      });
      const response = await fetch(`/api/inventory/listings/?${queryString}`);

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const {
        content,
        size,
        total,
      }: {
        content: VehicleListingModel[];
        size: number;
        total: number;
      } = await response.json();

      return {
        content: content.map((product: VehicleListingModel) =>
          parseListingVehicle(product)
        ),
        size,
        total,
      };
    },
  });

  const totalPages = Math.ceil((inventory?.total ?? 0) / PAGE_SIZE);

  useEffect(() => {
    if (inventory && !iframeVehicle) {
      setIframeVehicle(inventory.content[0]);
    }
  }, [inventory, iframeVehicle]);

  const handlePageClick = useCallback(
    (pageNum: number) => {
      setPage(Math.min(totalPages, Math.max(0, pageNum)));
      window.scrollTo({ top: 0 });
    },
    [totalPages]
  );

  const dealerInventoryProps: DealerInventoryProps = {
    loading: isLoading,
    dealerName: dealer.name,
    listings: inventory?.content ?? [],
    totalListings: inventory?.total ?? 0,
    filters,
    setFilters: (vals) => setFilters({ ...filters, ...vals }),
    availableMakes,
    availableModels: availableModels ?? {},
    pages: {
      current: page,
      total: totalPages,
      handleNext: () => handlePageClick(page + 1),
      handlePrevious: () => handlePageClick(page - 1),
      onChange: handlePageClick,
    },
  };

  const [editing, setEditing] = useState(false);
  const [metadata, setMetadata] = useState(dealerMetadata);
  const [draftMetadata, setDraftMetadata] = useState<EditableDealerInfo>({
    showBenefitsSection: dealerMetadata.showBenefitsSection,
    benefitsList:
      dealerMetadata.benefitsList.length !== 0
        ? dealerMetadata.benefitsList
        : defaultBenefits,
    detailedBenefits:
      dealerMetadata.detailedBenefits.length !== 0
        ? dealerMetadata.detailedBenefits
        : defaultDetailedBenefits,
    hoursOfOperation: dealerMetadata.hoursOfOperation ?? defaultHours,
    showHours: dealerMetadata.showHours,
  });

  const { mutate } = useMutation({
    mutationFn: async () => {
      const res = await putEditableDealerInfo({
        dealershipId: dealer.id,
        ...draftMetadata,
      });

      if (!res.ok) {
        throw new Error();
      }
    },
    onSettled: () => {
      setMetadata(draftMetadata);
      showToast('Successfully updated dealer data!', {
        type: 'success',
      });
    },
    onError: () => {
      showToast('Something went wrong updating your data. Please try again.', {
        type: 'error',
      });
    },
  });

  return (
    <div>
      {editable && (
        <DealerEditHeader
          editing={editing}
          setEditing={setEditing}
          onPublish={() => mutate()}
        />
      )}
      <GrayedOut gray={editing}>
        <DealerHeader makes={dealer.makes} dealerName={dealer.name} />
        <div className="flex items-center justify-between px-xl py-l m:hidden">
          <div>
            <FilterOverlay {...dealerInventoryProps} />
          </div>
          <div className="flex items-center gap-s">
            <div>EV Savings</div>
            <EVSavingsToggle />
          </div>
        </div>
        <div className="bg-neutral-100">
          <ResponsiveMargin>
            <DealerInventory {...dealerInventoryProps} />
          </ResponsiveMargin>
        </div>
        <div className="bg-gradient-to-r from-darkBrand-start to-darkBrand-end py-3xl">
          <ResponsiveMargin>
            {!zipLoading && iframeVehicle && (
              <SavingsToolV2
                vin={iframeVehicle.vin}
                isGas={iframeVehicle.fuelType !== 'Electric'}
                condition={iframeVehicle.condition}
                bodyType={iframeVehicle.body}
                knownInventory={true}
                defaultZip={preferredZip}
              />
            )}
          </ResponsiveMargin>
        </div>
      </GrayedOut>

      {/* Editable section */}
      <div className="bg-neutral-100">
        <ResponsiveMargin>
          {(editing ||
            metadata.detailedBenefits.length !== 0 ||
            metadata.benefitsList.length !== 0) && (
            <Benefits
              dealerName={dealer.name}
              editableDealerInfo={metadata}
              draftEditableDealerInfo={draftMetadata}
              setDraftEditableDealerInfo={setDraftMetadata}
              editing={editing}
            />
          )}
          <DealerLocation
            dealerName={dealer.name}
            location={dealer.location}
            phone={dealer.phone}
            website={dealer.website}
            editableDealerInfo={metadata}
            draftEditableDealerInfo={draftMetadata}
            setDraftEditableDealerInfo={setDraftMetadata}
            editing={editing}
          />
          <GrayedOut gray={editing}>
            <div
              className="pb-3xl text-microLight text-neutral-800 ml:pt-xl"
              data-nosnippet
            >
              *Disclaimer: Prices and EV savings estimates are for informational
              purposes only. Confirm final pricing, incentive programs, vehicle
              costs, and tax eligibility with the seller before purchasing.
              Monthly payment estimates are for convenience and do not
              constitute a financing offer or credit guarantee. All third-party
              trademarks are the property of their respective owners and do not
              imply endorsement or affiliation with EV.com.
            </div>
          </GrayedOut>
        </ResponsiveMargin>
      </div>
    </div>
  );
};

export default DealerV2Template;
